import { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { importService, ImportSource, FileRef } from '@/services/api/import';
import LocalDropZone from '../components/LocalDropZone';
import CloudFileBrowser from '../components/CloudFileBrowser';

export default function Step2Browse({
  source,
  onBack,
  onPicked,
}: {
  source: ImportSource;
  onBack: () => void;
  onPicked: (refs: FileRef[], files?: File[]) => void;
}) {
  const [selected, setSelected] = useState<FileRef[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleNext = async () => {
    if (source === 'local') {
      if (localFiles.length === 0) return;
      setUploading(true);
      try {
        // Get presigned URLs + upload to S3 staging
        const { files } = await importService.getLocalUploadUrls(
          localFiles.map(f => ({ filename: f.name, size: f.size, mimeType: f.type }))
        );
        await Promise.all(files.map(async (entry, i) => {
          await importService.uploadLocalFile(entry.uploadUrl, localFiles[i]);
        }));
        const refs: FileRef[] = files.map(f => ({
          stagingKey: f.stagingKey, name: f.filename, size: f.size, mimeType: f.mimeType,
        }));
        onPicked(refs, localFiles);
      } catch (e: any) {
        alert(`Upload failed: ${e.message || e}`);
      } finally {
        setUploading(false);
      }
    } else {
      onPicked(selected);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="text-xs text-muted-foreground">
          {source === 'local' ? `${localFiles.length} files chosen` : `${selected.length} files selected`}
        </div>
      </div>

      {source === 'local' ? (
        <LocalDropZone files={localFiles} onChange={setLocalFiles} />
      ) : (
        <CloudFileBrowser source={source} selected={selected} onChange={setSelected} />
      )}

      <div className="flex justify-end">
        <Button
          onClick={handleNext}
          disabled={(source === 'local' ? localFiles.length === 0 : selected.length === 0) || uploading}
          className="gap-2"
        >
          {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
          {uploading ? 'Uploading...' :
            `Detect ${source === 'local' ? localFiles.length : selected.length} file${(source === 'local' ? localFiles.length : selected.length) === 1 ? '' : 's'}`}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
